/**
 * VideoAnalysis — Hero page for athlete video capture and pose analysis review.
 *
 * Three tabs:
 *   Record   — Camera capture + session setup
 *   Review   — Browse past videos, filter by athlete/event
 *   Analysis — Select a video to view pose overlay + biomechanics dashboard
 */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import { useAuth } from '../context/AuthContext.js';
import { useAthletes } from '../hooks/useAthletes.js';
import { videoApi } from '../api/video.js';
import Card, { CardHeader } from '../components/ui/Card.js';
import Button from '../components/ui/Button.js';
import { Badge, StatusBadge } from '../components/ui/Badge.js';
import { Select } from '../components/ui/Input.js';
import { LoadingScreen } from '../components/ui/Spinner.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { Table } from '../components/ui/Table.js';
import VideoCapture from '../components/features/video/VideoCapture.js';
import RecordingSession from '../components/features/video/RecordingSession.js';
import UploadProgress from '../components/features/video/UploadProgress.js';
import AthleteSeedPicker from '../components/features/video/AthleteSeedPicker.js';
import { formatDate, scoreColor } from '../utils/formatters.js';
import { GYMNASTICS_EVENTS, RISK_CATEGORIES } from '../utils/constants.js';

// Capture/triage hub. Viewing a clip's analysis lives in the athlete's profile
// (Schedule & Videos tab) — the Review list deep-links there, so there is no
// separate in-page analysis viewer here.
const TABS = [
  { key: 'record', label: 'Record' },
  { key: 'review', label: 'Review Videos' },
];

export default function VideoAnalysis() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { athletes, loading: loadingAthletes } = useAthletes();

  const [tab, setTab] = useState('record');
  const [socket, setSocket] = useState(null);

  // Record tab state
  const [session, setSession] = useState(null); // { event, athleteIds: [] }
  const [activeAthleteId, setActiveAthleteId] = useState(null); // who the next clip belongs to
  const [counts, setCounts] = useState({}); // { athleteId: clipsCaptured }
  const [uploadState, setUploadState] = useState(null); // { videoId, uploadPct }
  const [uploading, setUploading] = useState(false);
  const [pendingClip, setPendingClip] = useState(null); // { file, name } awaiting athlete tap
  const [notice, setNotice] = useState(null); // { type:'error'|'warn'|'info', message } — inline banner, no blocking alert()
  // Landing Assessment: per-clip camera view + optional drop height. Only used
  // when the session event is 'landing' (drop-landing screen).
  const [landingView, setLandingView] = useState('sagittal');
  const [dropHeightCm, setDropHeightCm] = useState('');
  // Optional capture-time context (ML-training metadata) behind an Advanced
  // toggle. Empty fields are dropped before upload.
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [context, setContext] = useState({
    skill_name: '', context_type: '', surface_type: '',
    terrain_modifier: '', pass_number: '', fatigue: '', notes: '',
  });
  const setCtx = useCallback((k, v) => setContext(prev => ({ ...prev, [k]: v })), []);

  // Review tab state
  const [filterAthlete, setFilterAthlete] = useState('');
  const [videos, setVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);

  const importInputRef = useRef(null);

  // Stable per-device id so clips are traceable to the iPad/phone that captured them.
  const deviceId = useMemo(() => {
    try {
      let id = localStorage.getItem('ams_device_id');
      if (!id) {
        id = `dev-${Math.random().toString(36).slice(2, 8)}-${Date.now().toString(36)}`;
        localStorage.setItem('ams_device_id', id);
      }
      return id;
    } catch {
      return 'dev-unknown';
    }
  }, []);

  // ── Rotation session controls ────────────────────────────────
  const startSession = useCallback(({ event, athleteIds, captureSessionId = null }) => {
    setSession({ event, athleteIds, captureSessionId });
    setActiveAthleteId(athleteIds[0] ?? null);
    setCounts({});
    setUploadState(null);
  }, []);

  const endSession = useCallback(() => {
    setSession(null);
    setActiveAthleteId(null);
    setCounts({});
    setUploadState(null);
  }, []);

  // ── Socket.io ────────────────────────────────────────────────
  useEffect(() => {
    const baseURL = window.location.hostname !== 'localhost'
      ? `${window.location.protocol}//${window.location.hostname}`
      : 'http://localhost:5001';

    const s = io(baseURL);
    setSocket(s);
    return () => s.disconnect();
  }, []);

  // ── Load videos when review tab or filter changes ────────────
  const loadVideos = useCallback(async () => {
    if (!filterAthlete) { setVideos([]); return; }
    setLoadingVideos(true);
    try {
      const data = await videoApi.listForAthlete(filterAthlete);
      setVideos(data);
    } catch {
      setVideos([]);
      setNotice({ type: 'error', message: "Couldn't load videos — check your connection and try again." });
    } finally {
      setLoadingVideos(false);
    }
  }, [filterAthlete]);

  useEffect(() => {
    if (tab === 'review') loadVideos();
  }, [tab, loadVideos]);

  // ── Recording / import handlers ──────────────────────────────
  const uploadVideoFile = useCallback(async (fileOrBlob, filename, seed = null) => {
    if (!session || !activeAthleteId) return;
    const athleteId = activeAthleteId; // capture now; coach may switch during upload
    setUploading(true);
    setUploadState(null);

    const formData = new FormData();
    formData.append('video', fileOrBlob, filename);
    formData.append('athlete_id', athleteId);
    formData.append('event', session.event);
    formData.append('device_id', deviceId);
    // Landing Assessment: tag the camera view (+ optional drop height) so the
    // sagittal/frontal clips can be paired and scored by the analyzer.
    if (session.event === 'landing') {
      formData.append('view', landingView || '');
      if (dropHeightCm) formData.append('drop_height_cm', dropHeightCm);
    }
    // Optional coach-seeded athlete selection { frame_idx, point:[x,y] }.
    if (seed) formData.append('seed', JSON.stringify(seed));
    // Multi-camera grouping: clips sharing this code form a multi-angle set.
    if (session.captureSessionId) formData.append('capture_session_id', session.captureSessionId);
    // Optional Advanced context — drop empty fields before sending.
    const ctx = Object.fromEntries(
      Object.entries(context).filter(([, v]) => String(v).trim() !== '')
    );
    if (Object.keys(ctx).length) formData.append('context', JSON.stringify(ctx));

    try {
      const result = await videoApi.upload(formData, (pct) =>
        setUploadState(prev => ({ ...prev, uploadPct: pct }))
      );
      setUploadState({ videoId: result.videoId, uploadPct: 100, athleteId });
      setCounts(prev => ({ ...prev, [athleteId]: (prev[athleteId] || 0) + 1 }));
    } catch (err) {
      setNotice({ type: 'error', message: `Upload failed: ${err.response?.data?.error || err.message}. Try again.` });
    } finally {
      setUploading(false);
    }
  }, [session, activeAthleteId, deviceId, landingView, dropHeightCm, context]);

  // A finished clip first goes to the athlete-tap step, then uploads (with the
  // optional seed).  Skipping the tap uploads with no seed (auto-designate).
  const handleRecordingComplete = useCallback(
    (blob, _url, filename) => setPendingClip({ file: blob, name: filename || 'recording.webm' }),
    []
  );

  // Import an existing video (camera roll / device); analyzed the same way.
  const handleImportFile = useCallback((e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    if (!session || !activeAthleteId) {
      setNotice({ type: 'warn', message: 'Start a rotation and pick the active athlete first.' });
      return;
    }
    // Cloudflare rejects request bodies over ~100 MB at the edge (before our
    // server sees them), so guard client-side with headroom for a clean error.
    const MAX_BYTES = 90 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      setNotice({ type: 'warn', message: `That video is ${(file.size / 1024 / 1024).toFixed(0)} MB — the limit is 90 MB. Trim it or lower the resolution and try again.` });
      return;
    }
    setPendingClip({ file, name: file.name || 'import.mp4' });
  }, [session, activeAthleteId]);

  // ── Review columns ───────────────────────────────────────────
  const videoColumns = [
    { key: 'created_at', header: 'Date', render: r => formatDate(r.created_at) },
    { key: 'event', header: 'Event', render: r => GYMNASTICS_EVENTS.find(e => e.value === r.event)?.label || r.event || '—' },
    { key: 'analysis_status', header: 'Status', render: r => <StatusBadge status={r.analysis_status} /> },
    { key: 'quality_score', header: 'Quality', render: r => r.quality_score != null
      ? <span className={scoreColor(Number(r.quality_score))}>{Number(r.quality_score).toFixed(0)}</span>
      : '—'
    },
    { key: 'injury_risk_category', header: 'Risk', render: r => {
      const cat = RISK_CATEGORIES[r.injury_risk_category];
      return cat ? <Badge variant="default" className={cat.bg + ' ' + cat.color}>{cat.label}</Badge> : '—';
    }},
  ];

  const canRecord = ['admin', 'coach', 'athlete'].includes(user?.role);

  const roster = session ? athletes.filter(a => session.athleteIds.includes(a.id)) : [];
  const activeAthlete = athletes.find(a => a.id === activeAthleteId) || null;
  const eventLabel = GYMNASTICS_EVENTS.find(e => e.value === session?.event)?.label || session?.event || '';

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      {/* Inline, dismissible notice (replaces blocking alert()) */}
      {notice && (
        <div
          role="alert"
          className={[
            'mb-4 flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm',
            notice.type === 'error' ? 'bg-red-50 border-red-200 text-red-800'
              : notice.type === 'warn' ? 'bg-amber-50 border-amber-200 text-amber-800'
              : 'bg-blue-50 border-blue-200 text-blue-800',
          ].join(' ')}
        >
          <span>{notice.message}</span>
          <button
            onClick={() => setNotice(null)}
            aria-label="Dismiss"
            className="shrink-0 font-bold opacity-60 hover:opacity-100"
          >✕</button>
        </div>
      )}

      {/* Athlete-tap step before upload (skippable → auto-designate) */}
      {pendingClip && (
        <AthleteSeedPicker
          file={pendingClip.file}
          fps={30}
          onConfirm={(seed) => {
            const { file, name } = pendingClip;
            setPendingClip(null);
            uploadVideoFile(file, name, seed);
          }}
          onSkip={() => {
            const { file, name } = pendingClip;
            setPendingClip(null);
            uploadVideoFile(file, name, null);
          }}
        />
      )}

      {/* Page header */}
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Video Analysis</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Record athlete performances and review pose analysis
          </p>
        </div>
        {user?.role !== 'parent' && (
          <a
            href="/display"
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md bg-white text-primary border border-primary hover:bg-blue-50"
          >
            📺 Open Review Wall
          </a>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={[
              'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === t.key
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700',
            ].join(' ')}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── RECORD TAB ── */}
      {tab === 'record' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Session setup / active rotation */}
          <Card>
            {!session ? (
              <>
                <CardHeader title="Session Setup" subtitle="Pick the event and everyone in the rotation" />
                {loadingAthletes ? (
                  <LoadingScreen message="Loading athletes…" />
                ) : (
                  <RecordingSession
                    athletes={athletes}
                    loading={uploading}
                    onStart={startSession}
                  />
                )}
              </>
            ) : (
              <>
                <CardHeader
                  title={`Rotation · ${eventLabel}`}
                  subtitle="Tap who’s up next, then record their pass"
                />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {roster.map(a => {
                    const isActive = a.id === activeAthleteId;
                    const n = counts[a.id] || 0;
                    return (
                      <button
                        key={a.id}
                        onClick={() => setActiveAthleteId(a.id)}
                        className={[
                          'rounded-lg border p-2 text-center transition-colors',
                          isActive
                            ? 'border-primary bg-primary text-white'
                            : 'border-gray-200 bg-white text-gray-700 hover:border-primary',
                        ].join(' ')}
                      >
                        <div className="text-sm font-semibold truncate">{a.name}</div>
                        <div className={['text-xs mt-0.5', isActive ? 'text-blue-100' : 'text-gray-400'].join(' ')}>
                          {n} clip{n === 1 ? '' : 's'}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <Button variant="ghost" size="sm" className="mt-3" onClick={endSession}>
                  End Rotation
                </Button>
              </>
            )}
          </Card>

          {/* Camera */}
          <Card>
            <CardHeader title="Camera" />
            {!canRecord ? (
              <EmptyState
                icon="🎥"
                title="Recording not available"
                description="Only admins, coaches, and athletes can record videos."
              />
            ) : (
              <>
                {session && activeAthlete && (
                  <div className="mb-2 flex items-center gap-2 text-sm">
                    <span className="w-2 h-2 rounded-full bg-primary" />
                    <span className="text-gray-500">Up next:</span>
                    <span className="font-semibold text-gray-900">{activeAthlete.name}</span>
                  </div>
                )}

                {/* Landing Assessment: pick the camera view (and optional drop
                    height) for the next clip. Film sagittal + frontal per athlete. */}
                {session?.event === 'landing' && (
                  <div className="mb-3 grid grid-cols-2 gap-3">
                    <Select
                      label="Camera view"
                      value={landingView}
                      onChange={e => setLandingView(e.target.value)}
                    >
                      <option value="sagittal">Sagittal (side)</option>
                      <option value="frontal">Frontal (front)</option>
                    </Select>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Drop height (cm)
                      </label>
                      <input
                        type="number"
                        inputMode="numeric"
                        min="0"
                        value={dropHeightCm}
                        onChange={e => setDropHeightCm(e.target.value)}
                        placeholder="optional"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                )}

                <VideoCapture
                  disabled={!session || uploading}
                  onRecordingComplete={handleRecordingComplete}
                />
                {!session && (
                  <p className="text-xs text-gray-400 mt-2">
                    Start a rotation before recording.
                  </p>
                )}

                {/* Import an existing video from the device / camera roll */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <input
                    ref={importInputRef}
                    type="file"
                    accept="video/*"
                    className="hidden"
                    onChange={handleImportFile}
                  />
                  <Button
                    variant="secondary"
                    fullWidth
                    loading={uploading}
                    disabled={!session || !activeAthleteId || uploading}
                    onClick={() => importInputRef.current?.click()}
                  >
                    📁 Import from Camera Roll
                  </Button>
                  <p className="text-xs text-gray-400 mt-2">
                    Import an existing video instead of recording — it’s assigned to the
                    active athlete and analyzed the same way (iPhone clips included).
                  </p>
                </div>

                {/* Advanced (optional) capture context — ML-training metadata.
                    Collapsed by default so quick capture stays fast. */}
                {session && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => setShowAdvanced(v => !v)}
                      className="text-sm font-medium text-gray-600 hover:text-gray-900"
                    >
                      {showAdvanced ? '▼' : '▶'} Advanced context (optional)
                    </button>
                    {showAdvanced && (
                      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="sm:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Skill name</label>
                          <input
                            type="text"
                            value={context.skill_name}
                            onChange={e => setCtx('skill_name', e.target.value)}
                            placeholder="e.g. Double Layout, Yurchenko Full"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <Select label="Context" value={context.context_type} onChange={e => setCtx('context_type', e.target.value)}>
                          <option value="">—</option>
                          <option value="single_skill">Single Skill</option>
                          <option value="dismount">Dismount Only</option>
                          <option value="full_routine">Full Routine</option>
                          <option value="half_set">Half Set</option>
                          <option value="warmup">Warmup</option>
                          <option value="conditioning">Conditioning</option>
                        </Select>
                        <Select label="Surface" value={context.surface_type} onChange={e => setCtx('surface_type', e.target.value)}>
                          <option value="">—</option>
                          <option value="competition_floor">Competition Floor</option>
                          <option value="training_mat">Training Mat</option>
                          <option value="trampoline">Trampoline</option>
                          <option value="tumble_track">Tumble Track</option>
                          <option value="hard_floor">Hard Floor</option>
                          <option value="raised_floor">Raised Floor</option>
                          <option value="rod_floor">Rod Floor</option>
                          <option value="pit">Pit / Foam</option>
                        </Select>
                        {['floor_exercise', 'tumbling'].includes(session.event) && (
                          <Select label="Terrain" value={context.terrain_modifier} onChange={e => setCtx('terrain_modifier', e.target.value)}>
                            <option value="">—</option>
                            <option value="flat">Flat</option>
                            <option value="uphill">Uphill</option>
                            <option value="downhill">Downhill</option>
                            <option value="diagonal">Diagonal</option>
                          </Select>
                        )}
                        {context.context_type === 'full_routine' && (
                          <Select label="Pass number" value={context.pass_number} onChange={e => setCtx('pass_number', e.target.value)}>
                            <option value="">—</option>
                            <option value="1">Pass 1</option>
                            <option value="2">Pass 2</option>
                            <option value="3">Pass 3</option>
                            <option value="4">Pass 4</option>
                          </Select>
                        )}
                        <Select label="Fatigue" value={context.fatigue} onChange={e => setCtx('fatigue', e.target.value)}>
                          <option value="">—</option>
                          <option value="fresh">Fresh</option>
                          <option value="moderate">Moderate</option>
                          <option value="tired">Tired</option>
                          <option value="exhausted">Exhausted</option>
                        </Select>
                        <div className="sm:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5">Technical notes</label>
                          <textarea
                            value={context.notes}
                            onChange={e => setCtx('notes', e.target.value)}
                            rows={2}
                            placeholder="e.g. chest down, knees bent, early twist…"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </Card>

          {/* Upload / analysis progress */}
          {uploadState && (
            <div className="md:col-span-2">
              <Card>
                <CardHeader
                  title="Analysis Progress"
                  subtitle={uploadState.athleteId
                    ? athletes.find(a => a.id === uploadState.athleteId)?.name
                    : undefined}
                />
                <UploadProgress
                  videoId={uploadState.videoId}
                  uploadPct={uploadState.uploadPct}
                  socket={socket}
                  onComplete={() => { /* stay on Record so the rotation can continue */ }}
                />
              </Card>
            </div>
          )}
        </div>
      )}

      {/* ── REVIEW TAB ── */}
      {tab === 'review' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-end gap-4">
            <Select
              label="Athlete"
              value={filterAthlete}
              onChange={e => setFilterAthlete(e.target.value)}
              className="max-w-xs"
            >
              <option value="">Select athlete…</option>
              {athletes.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </Select>
            {filterAthlete && (
              <Button variant="secondary" size="sm" onClick={loadVideos}>
                Refresh
              </Button>
            )}
          </div>

          {!filterAthlete ? (
            <EmptyState
              icon="🎬"
              title="Select an athlete"
              description="Choose an athlete above to view their recorded videos."
            />
          ) : (
            <Table
              columns={videoColumns}
              rows={videos}
              loading={loadingVideos}
              emptyMessage="No videos recorded yet."
              onRowClick={(row) => {
                // Unify: open the clip in its canonical home — the athlete's
                // profile video calendar — instead of a parallel in-page viewer.
                const aid = row.athlete_id || filterAthlete;
                const date = row.created_at
                  ? new Date(row.created_at).toISOString().split('T')[0]
                  : '';
                navigate(`/athlete-card/${aid}?tab=videos${date ? `&date=${date}` : ''}`);
              }}
            />
          )}
        </div>
      )}

    </div>
  );
}
